import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import {
  createMapControlSlice,
  MapControlSlice,
  mapControlInitialState,
} from './Slices/MapControlSlice';
import { createMapInfoSlice, MapInfoSlice, mapInfoInitialState } from './Slices/MapInfoSlice';
import { createMapEventSlice, MapEventSlice, mapEventInitialState } from './Slices/MapEventSlice';
import {
  createMapObjectSlice,
  MapObjectSlice,
  mapObjectInitialState,
} from './Slices/MapObjectSlice';
import { createMapDataSlice, MapDataSlice, mapDataInitialState } from './Slices/MapDataSlice';
import { createMapUiSlice, MapUiSlice, mapUiInitialState } from './Slices/MapUiSlice';
import { createMapLayerSlice, MapLayerSlice, mapLayerInitialState } from './Slices/MapLayerSlice';

export interface MapStore
  extends MapControlSlice,
    MapInfoSlice,
    MapEventSlice,
    MapObjectSlice,
    MapDataSlice,
    MapUiSlice,
    MapLayerSlice {
  resetAllSlices: () => void;
}

export const useMapStore = create<MapStore>()(
  persist(
    (set, get, api) => ({
      // --- MapControlSlice: spread the slice to include all setters ---
      ...createMapControlSlice(set, get, api),

      // Override only non-persisted defaults
      showInfo: false,
      showMore: null,
      isDirty: false,
      edit: false,
      selected: 'asset',
      reading: 'temperature',

      // Persisted keys (mapLayers, markerSize, opacity) are NOT overridden here

      // --- Other slices ---
      ...createMapInfoSlice(set, get, api),
      ...createMapEventSlice(set, get, api),
      ...createMapObjectSlice(set, get, api),
      ...createMapDataSlice(set, get, api),
      ...createMapUiSlice(set, get, api),
      ...createMapLayerSlice(set, get, api),

      // --- Reset function ---
      resetAllSlices: () => {
        const mapKey = get().mapKey;

        set({
          // Non-persisted control slice keys
          showInfo: false,
          showMore: null,
          isDirty: false,
          edit: false,
          selected: 'asset',
          reading: 'temperature',

          // Other slices initial state
          ...mapInfoInitialState,
          ...mapEventInitialState,
          ...mapObjectInitialState,
          ...mapDataInitialState,
          ...mapUiInitialState,
          ...mapLayerInitialState,

          mapKey,
        });
      },
    }),
    {
      name: 'map-store', // localStorage key
      partialize: (state) => ({
        mapLayers: state.mapLayers,
        markerSize: state.markerSize,
        opacity: state.opacity,
      }),
    },
  ),
);
